import { Link } from "react-router-dom"
import { CONTACT, mailtoHref, telHref, whatsappHref } from "@/content/contact"
import { PageMeta } from "@/components/shared/PageMeta"
import { PAGE_SEO } from "@/lib/seo"
import { StaticPage } from "./StaticPages"

const sectionHeadingClass = "mt-10 text-balance text-lg font-semibold tracking-tight text-foreground sm:text-xl"

const linkClass = "text-primary underline-offset-4 hover:underline"

export function PrivacidadPage(): React.JSX.Element {
	return (
		<>
			<PageMeta {...PAGE_SEO.privacidad} path="/privacidad" />
			<StaticPage title="Política de Privacidad">
				<p>
					Valoramos la relación con nuestros clientes y nos comprometemos a observar prácticas responsables en cuanto al
					manejo de su información.
				</p>
				<p>
					Biosany.com (en adelante el &quot;sitio web de Biosany Shalósh Bayóm&quot;) y todos los demás lugares,
					ocasiones o eventos en los cuales se obtenga su información personal a cargo de o en nombre de Biosany Shalósh
					Bayóm. Al facilitarnos su información personal, usted acepta las prácticas sobre el tratamiento de la
					información descritas en esta política. Es posible que esta Política de Privacidad no se aplique a sitios Web
					a los que se accedan a través de los sitios de Biosany Shalósh Bayóm. Nuestros sitios Web contienen enlaces a
					otros sitios operados por terceros proveedores, incluyendo de manera enunciativa pero no limitativa a sitios
					de terceros que muestren las marcas de Biosany Shalósh Bayóm. Los animamos a leer las políticas de privacidad
					de esos otros sitios para saber cómo recopilar, usar, compartir y proteger su información.
				</p>

				<h2 className={sectionHeadingClass}>Información recopilada</h2>
				<p>
					Es posible que recopilemos información personal, como: información de contacto, incluyendo nombre, dirección
					postal, número de teléfono y dirección de e-mail; información de compra y envío (productos, montos, método de
					pago seleccionado y estado del pedido); e identificadores técnicos necesarios para la operación del sitio.
					También podremos recopilar información demográfica, incluyendo edad, intereses personales y preferencias de
					productos.
				</p>
				<p>
					<strong>Datos de pago.</strong> Cuando paga con tarjeta, wallet o efectivo OXXO a través de Stripe, o con
					PayPal, los datos sensibles del método de pago (por ejemplo, el número completo de la tarjeta o las
					credenciales de PayPal) los recibe y procesa el proveedor correspondiente. Biosany no almacena esa información
					completa. Conservamos referencias del pedido, el método elegido y, en su caso, identificadores de transacción
					que el proveedor nos comunica para conciliar el pago. En transferencias SPEI podemos asociar su pedido a la
					referencia y al comprobante o datos bancarios que usted nos proporcione para validar el depósito.
				</p>
				<p>
					Si ya es cliente y vuelve a comprar con el mismo correo electrónico, podemos enviarle un código de un solo uso
					(OTP) a ese correo para verificar su identidad antes de mostrar o reutilizar datos de contacto y envío
					guardados. Ese código tiene vigencia limitada y no sustituye las contraseñas de Stripe ni de PayPal.
				</p>
				<p>
					Podemos llegar a pedirle su información personal cuando esté comprando o aprovechando los servicios que
					ofrecemos. Por ejemplo, es posible que pidamos y/o recopilemos información personal cuando usted:
				</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>Compre productos o servicios</li>
					<li>Complete el checkout (datos de envío y método de pago)</li>
					<li>Verifique su correo electrónico con un código OTP</li>
					<li>Establezca una cuenta en línea</li>
					<li>Programe una entrega o instalación</li>
					<li>Participe en un sorteo, promoción o encuesta</li>
					<li>Comuníquese con nosotros si tiene preguntas o inquietudes</li>
					<li>Participe en otras actividades con Biosany Shalósh Bayóm</li>
				</ul>

				<h2 className={sectionHeadingClass}>Usos de su información personal</h2>
				<p>Usamos la información personal que usted nos suministra para propósitos que incluyen, pero no se limitan a:</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>Llenar pedidos de productos, servicios o información</li>
					<li>Rastrear y confirmar pedidos en línea</li>
					<li>Entregar o instalar productos</li>
					<li>Brindar servicio al cliente</li>
					<li>Administrar sorteos, promociones o encuestas</li>
					<li>Ofrecer nuevos productos y servicios</li>
					<li>
						Mejorar la efectividad de nuestros sitios Web, de nuestros esfuerzos de comercialización y nuestros
						servicios y ofertas
					</li>
					<li>Llevar a cabo investigaciones y análisis</li>
					<li>Enviar comunicaciones de comercialización</li>
					<li>
						Realizar otras actividades comerciales según sea necesario o según se describa en otras partes dentro de
						esta Política
					</li>
				</ul>

				<h3 className="mt-6 text-base font-semibold text-foreground sm:text-lg">
					La información de o sobre Amigos y Familia
				</h3>
				<p>
					Si usted nos proporciona información de otras personas, o si otras personas nos dan información sobre usted,{" "}
					<strong>solamente utilizaremos dicha información para la razón específica para la que se proporcionó</strong>.
					Algunos ejemplos incluyen proporcionar el domicilio de enviar publicidad semanal por correo electrónico a un
					amigo, y enviar por correo electrónico un enlace a los amigos para que visiten nuestro sitio web.
				</p>

				<h2 className={sectionHeadingClass}>Información de Terceros</h2>
				<p>
					Biosany Shalósh Bayóm obtiene información del consumidor de proveedores terceros para mejorar la exactitud de
					nuestra base de datos de clientes, para aumentar el entendimiento que tenemos de nuestros clientes, o para
					identificar posibles clientes.
				</p>

				<h2 className={sectionHeadingClass}>Compartir información personal</h2>
				<p>
					Biosany Shalósh Bayóm no vende ni alquila su información personal a terceros. Podemos tener la necesidad de
					compartir su información personal con terceros tales como nuestros agentes, proveedores de servicios y otros
					representantes que actúan en nuestro nombre, con fines limitados. Por ejemplo, podemos compartir información
					personal con terceros que realizan servicios en nombre nuestro como:
				</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>Ejecutar los pedidos de nuestros clientes</li>
					<li>Enviar comunicaciones comerciales de Biosany Shalósh Bayóm</li>
					<li>Entregar paquetes</li>
					<li>Programar y realizar instalaciones</li>
					<li>Proveer servicio a productos</li>
					<li>Cumplir servicios de suscripciones</li>
					<li>Realizar investigaciones, análisis o hacer encuestas</li>
					<li>Enviar correo regular y correo electrónico en nombre de Biosany Shalósh Bayóm</li>
					<li>
						Procesar pagos en línea, incluyendo Stripe (tarjetas, wallets y OXXO) y PayPal; cada proveedor trata los
						datos de pago conforme a su propia política de privacidad
					</li>
					<li>Validar transferencias bancarias SPEI asociadas a su pedido</li>
					<li>Enviar correos transaccionales (por ejemplo, códigos OTP o confirmaciones de compra)</li>
					<li>Proporcionar funciones de chat</li>
				</ul>
				<p>
					Los terceros con quien hayamos llevado a cabo nuestro negocio, están autorizados para usar su información
					solamente para dar el servicio por el que se les ha contratado. Están obligados a cumplir los términos de
					nuestra Política de Privacidad incluyendo tomar medidas razonables para asegurar que su información personal
					esté protegida. Cuando usted es redirigido a Stripe o PayPal, también aplican las políticas de privacidad de
					esos proveedores respecto de la información que les proporcione directamente. También es posible que
					compartamos datos agregados con terceros para propósitos de análisis e investigación. Estos datos no contienen
					nombre, dirección ni ninguna otra información personalmente identificable.
				</p>
				<p>
					De vez en cuando es posible que se nos requiera compartir información personal como respuesta a una orden
					judicial válida, a una investigación gubernamental o de otra manera requerida por la ley. También nos
					reservamos el derecho de informar a las agencias policíacas las actividades que de buena fe creamos ser
					ilegales. Compartiremos cierta información personal cuándo consideremos que tal divulgación sea razonablemente
					necesaria para proteger los derechos, la propiedad y la seguridad de otros y de nosotros mismos. Es posible
					que también transfiramos información personal en caso de una venta corporativa, fusión, adquisición,
					disolución o evento similar.
				</p>
				<p>
					Toda información que le identifique personalmente y que presente en un tablero de anuncios, blog, sala de
					chat, comentario sobre un producto, u otro foro público en este sitio Web, puede ser leída, recopilada o
					utilizada por otros usuarios de estos foros y podría ser utilizada para enviarle mensajes no solicitados. Le
					recomendamos que no dé su nombre, correo electrónico u otra información personal. Observe que si elige
					hacerlo, es bajo su propio riesgo y acepta la total responsabilidad de dichos comentarios y las consecuencias
					que puedan surgir de publicar dicha información.
				</p>
				<p>
					En ocasiones, podremos asociarnos con un tercero para crear un sitio que puede parecer como si estuviera en
					una página de www.Biosany.com. Estos sitios pueden compartir marca o bien, dar la apariencia y la sensación de
					estar en nuestro sitio. Sin embargo, la información recopilada en estas páginas puede no estar cubierta por la
					Política de Privacidad de Biosany Shalósh Bayóm. Informaremos de estos sitios y publicaremos la política de
					privacidad que sea aplicable a la información recopilada en estas páginas.
				</p>

				<h2 className={sectionHeadingClass}>Elecciones respecto a su información personal</h2>
				<p>
					Biosany Shalósh Bayóm se comunica con nuestros clientes a través de múltiples canales y medios para mantenerle
					actualizado de promociones, ofertas, contenido de la experiencia del cliente y productos o servicios nuevos.
					Una vez que usted proporciona su información personal, podemos ponernos en contacto con usted por correo
					directo, correo electrónico o teléfono. Podremos seguir comunicándonos con usted por medio de estos canales a
					no ser que usted solicite dejar de recibir estas comunicaciones optando ser excluido. Antes de enviarle
					publicidad móvil (por ejemplo, mensajes de texto), le pediremos su consentimiento expreso solicitándole su
					participación.
				</p>
				<p>
					Si ya no desea recibir estas comunicaciones, puede hacérnoslo saber mediante cualquiera de los siguientes
					métodos que sean apropiados bajo las circunstancias:
				</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>
						Siga las instrucciones en un correo electrónico o correo directo de publicidad que reciba de nosotros.
					</li>
					<li>
						Comuníquese con nosotros al correo{" "}
						<a href={mailtoHref()} className={linkClass}>
							{CONTACT.email}
						</a>
						, teléfono{" "}
						<a href={telHref} className={linkClass}>
							{CONTACT.phone}
						</a>{" "}
						o WhatsApp{" "}
						<a href={whatsappHref()} className={linkClass} rel="noopener noreferrer" target="_blank">
							{CONTACT.whatsapp}
						</a>
						. También puede visitar la{" "}
						<Link to="/contacto" className={linkClass}>
							página de contacto
						</Link>
						; tenga a la mano su información de contacto actual.
					</li>
				</ul>
				<p>
					Si envía una solicitud por correo electrónico o por carta, por favor asegúrese de incluir su nombre,
					dirección, número de teléfono y correo electrónico, al igual que los cambios específicos que quiere hacer.
					Esto asegurará que le identifiquemos correctamente en nuestros sistemas y que procesemos su petición
					correctamente.
				</p>
				<p>
					Biosany Shalósh Bayóm tomará las medidas necesarias para modificar sus presencias con respecto a nuestras
					comunicaciones de publicidad. Debido a las líneas de tiempo de producción, correos y sistemas, es posible que
					se tome algún tiempo para procesar su solicitud. Las siguientes pautas se ofrecen para su conveniencia:
				</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>10 días hábiles para correos electrónicos</li>
					<li>30 días hábiles para correo directo y teléfono</li>
				</ul>
				<p>
					Hasta que entre en vigor el cambio que haya solicitado, es posible que siga recibiendo comunicaciones de
					publicidad de nuestra parte. Si envía su solicitud por correo regular, por favor conceda tiempo adicional para
					que recibamos y procesemos su solicitud. Aunque haya optado ser excluido de las comunicaciones de publicidad,
					es posible que siga recibiendo comunicaciones comerciales tales como confirmaciones de pedidos, notificaciones
					de retiros del mercado, información sobre las membresías y otras comunicaciones relacionadas a la
					organización.
				</p>

				<h2 className={sectionHeadingClass}>Actualizando su información personal</h2>
				<p>
					Biosany Shalósh Bayóm desea que su información personal esté completa y al día. Ofrecemos varios métodos
					diferentes para que pueda actualizar su información personal. Para actualizar su nombre, dirección, número de
					teléfono y/o información de e-mail:
				</p>
				<ul className="list-disc space-y-2 pl-5">
					<li>
						Si ha creado una cuenta en Biosany.com, puede actualizar la información de su cuenta en línea después de
						ingresar a su cuenta.
					</li>
					<li>
						Si envía una solicitud por correo electrónico, por favor asegúrese de incluir su información actual y los
						cambios que se solicitan. Daremos una respuesta a su solicitud en 30 días.
					</li>
				</ul>

				<h2 className={sectionHeadingClass}>Seguridad de su información personal</h2>
				<p>
					Ya sea que compre en línea o en nuestros Centros de Salud, usamos medidas de seguridad razonables para
					proteger la confidencialidad de su información personal bajo nuestro control y limitamos el acceso a la misma
					apropiadamente. Biosany Shalósh Bayóm no puede asegurar ni garantizar la seguridad de la información que nos
					transmita por correo electrónico; y usted lo hace a su propio riesgo.
				</p>

				<h2 className={sectionHeadingClass}>Política de Compras Seguras</h2>
				<p>
					Usamos una variedad de medidas de seguridad de información para proteger sus transacciones en línea con
					nosotros. El sitio Web de Biosany Shalósh Bayóm usa cifrado (HTTPS/TLS) para proteger la información personal
					que usted envía, como datos de contacto, dirección de envío y detalles del pedido. Los datos sensibles del
					método de pago se capturan en los entornos seguros de Stripe o PayPal cuando usted elige esos métodos; no
					ingrese números de tarjeta ni contraseñas de PayPal en formularios ajenos a esos proveedores. En pagos por
					transferencia SPEI, solo le pedimos los datos necesarios para identificar su depósito. La información que
					usted nos dé por medio de nuestro quiosco en la tienda no atraviesa el Internet público.
				</p>

				<h2 className={sectionHeadingClass}>Elegir una Contraseña Personal</h2>
				<p>
					Al crear una cuenta en Biosany.com se requiere que establezca una contraseña. Para maximizar el nivel de
					protección, debe elegir al menos una combinación de letras y números. Es completamente responsabilidad suya
					mantener su contraseña y la información de su cuenta en secreto. Usted puede comprar en nuestro sitio web sin
					crear una cuenta, ahora bien, tendrá que reingresar su información personal cada vez que use nuestro sitio web
					para hacer una compra.
				</p>

				<h2 className={sectionHeadingClass}>Cookies</h2>
				<p>
					Un cookie es un pequeño archivo de datos que el sitio Web o un correo electrónico guarda en su buscador, que
					luego se puede guardar en su disco duro. Nuestro sitio Web usa cookies para crear una experiencia de compras
					más personalizada para los que visitan nuestro sitio. Las cookies simulan una conexión continua — nos permiten
					&quot;recordar&quot; la información sobre sus preferencias y sesión, y desplazarse dentro de las áreas de
					nuestros sitios Web sin volver a introducir sus datos. A pesar que nuestras cookies contiene un número único
					de usuario, no recopilan ni guardan ninguna información personal identificable. Usted puede configurar su
					navegador de Internet para que no acepte cookies. Ahora bien, si no se activan los cookies, no podrá ver,
					crear una cuenta, ni hacer compras por medio del sitio Web de Biosany Shalósh Bayóm.
				</p>

				<h2 className={sectionHeadingClass}>Anuncios comerciales de terceros y publicidad basada en intereses</h2>
				<p>
					Usamos compañías terceras de publicidad para ofrecer anuncios cuando visita nuestro sitio web u otros sitios
					en Internet. Estas compañías pueden usar sus tecnologías de seguimiento y cookies para recopilar información
					anónima (sin incluir su nombre, dirección, dirección de e-mail ni número telefónico) acerca de sus visitas a
					este y otros sitios web. También podremos compartir información anónima con estas compañías de manera
					agregada. En cualquier caso, el propósito de compartir es brindar anuncios sobre productos y servicios de su
					interés, analizar y mejorar la efectividad de los anuncios y correos electrónicos y mejorar y realzar nuestro
					sitio Web y su contenido. Para saber más sobre publicidad basada en intereses y su privacidad, incluyendo la
					exclusión voluntaria de dicha publicidad, visite el sitio Web de nuestros asociados.
				</p>
				<p>
					Podemos asociarnos con otras compañías para proporcionar publicidad basada en intereses, tanto dentro como
					fuera de Biosany.com. En estas situaciones, se le notificará de la publicidad y le daremos la oportunidad de
					optar por excluirse de estas actividades.
				</p>

				<h2 className={sectionHeadingClass}>Recopilación de información personal de niños</h2>
				<p>
					Tenemos el compromiso de proteger la privacidad de los niños. No recopilamos información personal en línea de
					niños menores a 13 años en forma deliberada o intencional.
				</p>

				<h2 className={sectionHeadingClass}>Cambios a la política de privacidad</h2>
				<p>
					A medida que Biosany Shalósh Bayóm sigue mejorando los servicios, productos y atributos que ofrecemos, esta
					política queda sujeta a cambios. Publicaremos los cambios importantes a esta Política, aquí y/o en otros
					formatos para su revisión. Si cambiamos nuestras prácticas de información o esta Política de Privacidad de tal
					manera que su información personal se pueda usar o compartir en una manera que no se describe en la Política
					de Privacidad al momento que usted suministró dicha información, le daremos la oportunidad de ser excluido de
					manera que su información no se comparta de esa nueva manera.
				</p>

				<h2 className={sectionHeadingClass}>Cómo comunicarse con nosotros</h2>
				<p>
					Si tiene preguntas sobre la Política de Privacidad de Biosany Shalósh Bayóm, comuníquese con nosotros al
					correo{" "}
					<a href={mailtoHref()} className={linkClass}>
						{CONTACT.email}
					</a>
					, teléfono{" "}
					<a href={telHref} className={linkClass}>
						{CONTACT.phone}
					</a>{" "}
					o WhatsApp{" "}
					<a href={whatsappHref()} className={linkClass} rel="noopener noreferrer" target="_blank">
						{CONTACT.whatsapp}
					</a>
					. También puede visitar la{" "}
					<Link to="/contacto" className={linkClass}>
						página de contacto
					</Link>
					.
				</p>

				<hr className="my-10 border-border" />

				<p className="text-sm italic">Última fecha de actualización: 26 de julio de 2026.</p>
			</StaticPage>
		</>
	)
}
